import { useState } from 'react';
import { Avatar, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import api from '../../services/api';
import { text } from '../../theme/colors';

const INK = '#1e293b';

/**
 * The professional photo realtors and conveyancers must upload (owner,
 * 2026-09-26). Uploads the image, then hands the stored url to `onUploaded`
 * (the profile save writes it). `previewUrl` is what to show now.
 */
export default function ProfessionalPhotoField({ previewUrl, name, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [local, setLocal] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Choose a photo (JPG or PNG).'); return; }
    setBusy(true); setError(''); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/uploads/profile-picture', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      setLocal(URL.createObjectURL(file));
      await onUploaded(r.data?.data?.url);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'The photo did not upload. Try again.');
    } finally { setBusy(false); }
  };

  const shown = local || previewUrl;
  return (
    <Box>
      <Stack direction="row" spacing={2.5} alignItems="center">
        <Avatar src={shown || undefined} alt={name || 'Your photo'} sx={{ width: 96, height: 96, bgcolor: '#134e4a', fontSize: 32 }}>
          {(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
        </Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, color: INK }}>
            Professional photo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mb: 1, maxWidth: 440 }}>
            Required. A clear, recent head-and-shoulders photo of you, facing the camera. Buyers see it on your card in the directory.
          </Typography>
          <Button component="label" variant="outlined" startIcon={<PhotoCamera />} disabled={busy}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}>
            {shown ? 'Change photo' : 'Upload photo'}
            <input hidden type="file" accept="image/*" onChange={pick} />
          </Button>
        </Box>
      </Stack>
      {busy && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1.5, maxWidth: 420 }} />}
      {!shown && !busy && <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>No photo yet: your account cannot be approved, and you do not appear in the directory, until you add one.</Typography>}
      {error && <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>{error}</Typography>}
    </Box>
  );
}
